import type React from 'react';
import type {
  RegistryShape,
  KindOf,
  VersionOf,
  DataFor,
  ViewProps,
} from '../Interfaces/GenericViews/IGenericView';
import { relationshipViewRegistry } from '../Interfaces/ILegalTransactionRelationship/IRelationshipData/IRelationshipDataViewRegistry';

// union über alle Versionen eines Kinds
type AnyDataForKind<R extends RegistryShape, K extends KindOf<R>> = {
  [V in VersionOf<R, K>]: DataFor<R, K, V>;
}[VersionOf<R, K>];

export function createRegistryView<R extends RegistryShape>(registry: R) {
  type Kind = KindOf<R>;

  type Props<K extends Kind = Kind> = {
    kind: K;
    version: VersionOf<R, K>;
    data: AnyDataForKind<R, K>;
    setData: (data: AnyDataForKind<R, K>) => void;
  };

  const BoundRegistryView = <K extends Kind>({
    kind,
    version,
    data,
    setData,
  }: Props<K>) => {
    const byKind = registry[kind as any];
    const entry =
      (byKind as any)?.[version] ?? (byKind as any)?.[String(version)];

    if (!entry) return null;

    const ViewComponent = entry.View as React.FC<ViewProps<any>>;
    return <ViewComponent data={data} setData={setData} />;
  };

  return BoundRegistryView;
}

export const RelationshipDataView = createRegistryView(
  relationshipViewRegistry
);
